/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Grid, Tooltip, Typography } from '@mui/material';
import React from 'react';
import { SearchHighlight } from '@opengeoweb/shared';
import type { layerTypes } from '@opengeoweb/store';
import DatasetAddRemoveButton from './DatasetAddRemoveButton';
import type { DiscoveryMetadataFeature } from '../../store/types';
import { useDataExplorerTranslation } from '../../utils/i18n';
import { DataExplorerInfoButtonConnect } from '../DataExplorerInfo/DataExplorerInfoButtonConnect';

interface DatasetRowProps {
  addLayer: () => void;
  deleteLayer: () => void;
  feature: DiscoveryMetadataFeature;
  searchFilter: string;
  addDisabled?: boolean;
  foundLayer?: layerTypes.Layer | null;
  layerName?: string | null;
}

const DatasetRow: React.FC<DatasetRowProps> = ({
  addLayer,
  deleteLayer,
  feature,
  searchFilter,
  addDisabled = false,
  foundLayer,
  layerName,
}: DatasetRowProps) => {
  const { t } = useDataExplorerTranslation();

  if (!feature) {
    return null;
  }

  return (
    <Grid
      container
      data-testid={`datasetRow-${feature.id}`}
      key={feature.id}
      size={12}
      sx={{
        backgroundColor: 'geowebColors.cards.cardContainer',
        padding: '6px',
        marginBottom: '3px',
        border: 'solid 1px',
        height: '64px',
        borderColor: 'geowebColors.cards.cardContainerBorder',
        '@container (max-width:300px)': {
          height: '196px',
          flexDirection: 'column',
        },
      }}
    >
      <Grid container direction="column" size="grow">
        <Grid size={12}>
          <Typography
            color="textPrimary"
            sx={{
              fontWeight: 550,
              fontSize: '12px',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 1,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              '@container (max-width:300px)': {
                WebkitLineClamp: 3,
                whiteSpace: 'normal',
              },
            }}
          >
            {!!feature.properties.title && (
              <SearchHighlight
                search={searchFilter}
                text={feature.properties.title!}
              />
            )}
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography
            color="textPrimary"
            sx={{
              fontSize: '12px',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'normal', // Allow text to wrap
              '@container (max-width:300px)': {
                WebkitLineClamp: 5,
              },
            }}
          >
            {!!feature.properties.description && (
              <SearchHighlight
                search={searchFilter}
                text={feature.properties.description!}
              />
            )}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={3}
        sx={{
          height: '100%',
          justifyContent: 'flex-end',
          alignItems: 'center',
          paddingRight: '6px',
          '@container (max-width:300px)': {
            width: '100%',
            height: '32px',
            justifyContent: 'space-between',
          },
        }}
      >
        <Tooltip title={t('dataexplorer-documentation')}>
          <span>
            <DataExplorerInfoButtonConnect id={feature.id} />
          </span>
        </Tooltip>

        <DatasetAddRemoveButton
          addLayer={addLayer}
          deleteLayer={deleteLayer}
          disabled={addDisabled}
          foundLayer={foundLayer!}
          layerName={layerName!}
        />
      </Grid>
    </Grid>
  );
};

export default DatasetRow;
